import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const DocumentHistory = ({ booking, onRegenerateQuote, onRegenerateInvoice }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, [booking.id]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${booking.id}/documents`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching document history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerate = (docType) => {
    if (docType === "quote") {
      onRegenerateQuote(booking);
    } else {
      onRegenerateInvoice(booking);
    }
    // Refresh history after a short delay
    setTimeout(fetchHistory, 1000);
  };

  const getDocumentTypeLabel = (type) => {
    return type === "quote" ? "Devis" : "Facture";
  };

  const getDocumentIcon = (type) => {
    return type === "quote" ? Download : FileText;
  };

  if (loading) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-sm">Historique des documents</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Chargement...</p>
        </CardContent>
      </Card>
    );
  }

  if (history.length === 0) {
    return (
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-sm">Historique des documents</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">Aucun document généré pour cette réservation</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Clock className="h-4 w-4" />
          Historique des documents
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {history.map((doc) => {
            const Icon = getDocumentIcon(doc.document_type);
            return (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-4 w-4 text-gray-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {getDocumentTypeLabel(doc.document_type)}
                    </p>
                    <p className="text-xs text-gray-500">
                      Généré le {format(parseISO(doc.generated_at), "dd MMM yyyy 'à' HH:mm", { locale: fr })}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleRegenerate(doc.document_type)}
                  className="text-xs"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Retélécharger
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
